import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, Steps, Timeline, Button, Modal, Input, Row, Col, message, Spin, Typography, Tag, Space, Avatar, Divider, Tooltip, Empty, Alert } from "antd";
import { 
  CheckCircleOutlined, 
  CloseCircleOutlined, 
  ClockCircleOutlined, 
  RightCircleOutlined,
  SafetyCertificateOutlined,
  HistoryOutlined,
  MessageOutlined,
  ArrowLeftOutlined
} from "@ant-design/icons";
import { fetchApprovalInstance, progressApprovalInstance, fetchUsers, fetchEntityTypes } from "../../api";
import useDecisionForm from "../../hooks/useDecisionForm";
import ApproverPaymentSplits from "../../components/ApproverPaymentSplits";
import RequestDetailsCard from "../../components/RequestDetailsCard";
import EntityPreviewCard from "../../components/EntityPreviewCard";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { Step } = Steps;

const STATUS_COLORS = {
  pending: "blue",
  approved: "success",
  rejected: "error",
  escalated: "warning",
  wait: "default"
};

// The instance's own status, which uses a different vocabulary from a step's.
const INSTANCE_STATUS = {
  pending: { color: "blue", label: "PENDING" },
  completed: { color: "green", label: "APPROVED" },
  approved: { color: "green", label: "APPROVED" },
  rejected: { color: "red", label: "REJECTED" },
  escalated: { color: "orange", label: "ESCALATED" },
  withdrawn: { color: "default", label: "WITHDRAWN" },
};

// Whether the business callback behind a terminal decision actually ran. A
// decision can be APPROVED while the money never moved — that gap is exactly
// what this tag exists to make visible.
const CALLBACK_TAG = {
  SUCCESS: "green", PROCESSING: "blue", PENDING: "default",
  RETRYING: "orange", FAILED: "red", PERMANENT_FAILURE: "red", NOT_APPLICABLE: "default",
};

export const ApprovalInstanceView = (props) => {
  const params = useParams();
  const navigate = useNavigate();
  const instanceId = props.instanceId || params.instanceId;
  const module = props.module || "Approval";
  // Rendered as a route (from the My Approvals inbox) rather than embedded in a
  // list screen — only then does a "Back to My Approvals" button make sense.
  const isStandalonePage = !props.instanceId;

  const [instance, setInstance] = useState(null);
  const [entityTypes, setEntityTypes] = useState([]);
  const [usersMap, setUsersMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [actionModal, setActionModal] = useState({ visible: false, type: null });
  const [actionComment, setActionComment] = useState("");
  // Input this decision needs at decision time — the bank UTRs for a payment
  // approval. Empty ({ form: null }) for every other entity type.
  const decision = useDecisionForm(instance?._id, {
    enabled: actionModal.visible && actionModal.type === "approved",
  });
  const [actionLoading, setActionLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  // Fetch instance and users
  useEffect(() => {
    const fetchData = async () => {
      if (!instanceId) return;
      setLoading(true);
      try {
        const fetchParams = { instanceId, page: 1, limit: 10 };
        if (props.module) fetchParams.module = props.module;
        
        const data = await fetchApprovalInstance(fetchParams);
        setInstance((Array.isArray(data) ? data[0] : data?.data?.[0]) || null);
        
        const usersRes = await fetchUsers();
        const map = {};
        (usersRes.data || []).forEach(u => (map[u._id] = u));
        setUsersMap(map);
      } catch (err) {
        messageApi.error("Failed to load approval instance");
      }
      setLoading(false);
    };
    fetchData();
  }, [instanceId, module]);

  // Registry metadata drives the "what is this for" panel (labels, columns, deep
  // link). Not fatal if the user lacks the permission — the panel falls back to
  // rendering raw displayMeta.
  useEffect(() => {
    fetchEntityTypes()
      .then((types) => setEntityTypes(Array.isArray(types) ? types : []))
      .catch(() => setEntityTypes([]));
  }, []);

  if (loading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!instance) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        {contextHolder}
        <Empty description="Approval case not found, or you don't have access to it." />
        <Button type="primary" style={{ marginTop: 16 }} onClick={() => window.history.back()}>Go back</Button>
      </div>
    );
  }

  // Current user info
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const currentStep = instance.steps.find(s => s.stepNumber === instance.currentStep);

  // What is being approved — registry metadata + the request's own business ref,
  // so the header names the thing instead of showing a bare ObjectId.
  const instanceEntityType = instance.entityType || instance.requestId?.entityType || instance.module;
  const entityMeta = entityTypes.find(e => e.entityType === instanceEntityType) || null;
  const headerLabel = entityMeta?.displayName
    || (module !== "Approval" ? module : "Request");
  const headerRef = instance.requestId?.businessRef
    || instance.objectId
    || instance._id.substring(0, 8);

  const instanceStatus = INSTANCE_STATUS[String(instance.status).toLowerCase()]
    || { color: 'default', label: String(instance.status || '').toUpperCase() };

  // Permission logic
  const canApprove = currentStep?.approvers?.includes(user.id) && currentStep.status === "pending";
  const canReject = canApprove;
  const canEscalate = canApprove;

  // Action handlers
  const handleAction = type => {
    setActionModal({ visible: true, type });
    setActionComment("");
  };

  const handleActionOk = async () => {
    if (!actionComment.trim() && actionModal.type !== 'approved') {
      messageApi.error("Please provide a comment");
      return;
    }
    // A payment cannot be approved until every bank split carries its UTR.
    if (decision.blocked) {
      messageApi.error(decision.errors[0]);
      return;
    }
    
    setActionLoading(true);
    try {
      await progressApprovalInstance({
        instanceId: instance._id,
        action: actionModal.type,
        comment: actionComment,
        payload: decision.payload,
      });
      messageApi.success(`Approval ${actionModal.type} successfully`);
      setActionModal({ visible: false, type: null });
      
      // Refresh instance
      const fetchParams = { instanceId, page: 1, limit: 10 };
      if (props.module) fetchParams.module = props.module;
      const data = await fetchApprovalInstance(fetchParams);
      setInstance((Array.isArray(data) ? data[0] : data?.data?.[0]) || null);
    } catch (err) {
      messageApi.error(err?.response?.data?.message || "Failed to update approval");
    }
    setActionLoading(false);
  };

  const handleActionCancel = () => {
    setActionModal({ visible: false, type: null });
    setActionComment("");
  };

  // Helper for initials avatar
  const getInitials = (name) => {
    if (!name) return "U";
    return name.split(" ").map(n => n[0]).join("").substring(0, 2).toUpperCase();
  };

  return (
    <div style={{ maxWidth: 1360, margin: '0 auto', padding: '24px 0' }}>
      {contextHolder}

      {isStandalonePage && (
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/approvals/my-approvals')}
          style={{ paddingLeft: 0, marginBottom: 4 }}
        >
          Back to My Approvals
        </Button>
      )}

      {/* Page Header */}
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            <SafetyCertificateOutlined style={{ color: '#1677ff', marginRight: 12 }} />
            Approval Workflow
          </Title>
          <Space size={8} wrap style={{ marginTop: 4 }}>
            <Text type="secondary" style={{ fontSize: 16 }}>
              {headerLabel} <Divider type="vertical" /> Ref: {headerRef}
            </Text>
            <Tag color={instanceStatus.color}>{instanceStatus.label}</Tag>
            {/* Only meaningful once a decision has landed — until then the
                callback has nothing to run. */}
            {instance.callbackState && instance.callbackState !== 'NOT_APPLICABLE' && (
              <Tooltip title={instance.lastCallbackError || 'Execution state of the business action behind this approval'}>
                <Tag color={CALLBACK_TAG[instance.callbackState] || 'default'}>
                  Callback: {instance.callbackState}
                </Tag>
              </Tooltip>
            )}
          </Space>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          {canApprove && (
            <Button type="primary" icon={<CheckCircleOutlined />} onClick={() => handleAction("approved")} size="large" style={{ borderRadius: 8 }}>
              Approve
            </Button>
          )}
          {canReject && (
            <Button danger icon={<CloseCircleOutlined />} onClick={() => handleAction("rejected")} size="large" style={{ borderRadius: 8 }}>
              Reject
            </Button>
          )}
          {canEscalate && (
            <Button onClick={() => handleAction("escalated")} icon={<RightCircleOutlined />} size="large" style={{ borderRadius: 8 }}>
              Escalate
            </Button>
          )}
        </div>
      </div>

      {/* The record itself, in full: every journal line, every fund the entry
          moves. Full page width — a ten-line double-entry does not fit in a
          sidebar column. Renders nothing for entity types whose module has not
          registered a previewProvider. */}
      <EntityPreviewCard instanceId={instance._id} style={{ marginBottom: 24 }} />

      <Row gutter={[24, 24]}>
        {/* Left Column - Steps & Details */}
        <Col xs={24} lg={16}>
          {/* What is being approved — rendered before the workflow, since that is
              the question an approver has first. */}
          <div style={{ marginBottom: 24 }}>
            <RequestDetailsCard instance={instance} entityMeta={entityMeta} />
          </div>

          <Card
            title={<><ClockCircleOutlined /> Approval Steps</>}
            bordered={false} 
            style={{ borderRadius: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', marginBottom: 24 }}
            headStyle={{ borderBottom: '1px solid #f0f0f0', padding: '16px 24px' }}
            bodyStyle={{ padding: 24 }}
          >
            <Steps
              direction="vertical"
              current={instance.currentStep - 1}
              style={{ padding: '0 12px' }}
            >
              {instance.steps.map((step, idx) => (
                <Step
                  key={idx}
                  title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                      <Text strong style={{ fontSize: 16 }}>{step.stepName}</Text>
                      <Tag color={STATUS_COLORS[step.status] || 'default'} style={{ borderRadius: 4, textTransform: 'uppercase', fontSize: 10 }}>
                        {step.status}
                      </Tag>
                    </div>
                  }
                  description={
                    <div style={{ 
                      background: step.status === 'active' || step.status === 'pending' ? '#f0f5ff' : '#fafafa',
                      padding: 16, 
                      borderRadius: 8, 
                      marginTop: 8, 
                      marginBottom: 24,
                      border: step.status === 'active' || step.status === 'pending' ? '1px solid #adc6ff' : '1px solid #f0f0f0' 
                    }}>
                      <Row gutter={[16, 16]}>
                        <Col span={24}>
                          <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Role Required</Text>
                          <Text strong>{step.role?.replace(/_/g, ' ') || 'Any'}</Text>
                        </Col>
                        <Col span={24}>
                          <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>Assigned Approvers</Text>
                          <Avatar.Group maxCount={4} size="default" maxStyle={{ color: '#f56a00', backgroundColor: '#fde3cf' }}>
                            {step.approvers.map((uid) => {
                              const approver = typeof uid === 'object' ? uid : usersMap[uid];
                              const approverName = approver?.name || approver?.email || String(uid);
                              return (
                                <Tooltip title={approverName} placement="top" key={String(uid)}>
                                  <Avatar style={{ backgroundColor: '#1677ff' }}>
                                    {getInitials(approverName)}
                                  </Avatar>
                                </Tooltip>
                              );
                            })}
                          </Avatar.Group>
                        </Col>
                        
                        {(step.startedAt || step.completedAt) && (
                          <Col span={24}>
                            <Divider style={{ margin: '8px 0' }} />
                            <Space split={<Divider type="vertical" />} size="large">
                              {step.startedAt && (
                                <div>
                                  <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Started At</Text>
                                  <Text style={{ fontSize: 13 }}>{dayjs(step.startedAt).format("DD MMM YYYY, hh:mm A")}</Text>
                                </div>
                              )}
                              {step.completedAt && (
                                <div>
                                  <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Completed At</Text>
                                  <Text style={{ fontSize: 13 }}>{dayjs(step.completedAt).format("DD MMM YYYY, hh:mm A")}</Text>
                                </div>
                              )}
                            </Space>
                          </Col>
                        )}
                        
                        {/* Display Comments for this step */}
                        {step.comments && step.comments.length > 0 && (
                          <Col span={24}>
                            <Divider style={{ margin: '8px 0' }} />
                            <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}><MessageOutlined /> Step Comments</Text>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                              {step.comments.map((c, i) => (
                                <div key={i} style={{ background: '#fff', padding: 12, borderRadius: 8, border: '1px solid #e8e8e8' }}>
                                  <Space align="start" size={12}>
                                    <Avatar size="small" style={{ backgroundColor: '#87d068' }}>
                                      {getInitials(usersMap[c.user]?.name)}
                                    </Avatar>
                                    <div>
                                      <div style={{ marginBottom: 4 }}>
                                        <Text strong style={{ fontSize: 13 }}>{usersMap[c.user]?.name || 'User'}</Text>
                                        <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                                          {c.date ? dayjs(c.date).format("DD MMM, hh:mm A") : ''}
                                        </Text>
                                      </div>
                                      <Text style={{ fontSize: 14 }}>{c.comment}</Text>
                                    </div>
                                  </Space>
                                </div>
                              ))}
                            </div>
                          </Col>
                        )}
                      </Row>
                    </div>
                  }
                  status={
                    step.status === "approved"
                      ? "finish"
                      : step.status === "rejected"
                      ? "error"
                      : step.status === "pending"
                      ? "process"
                      : "wait"
                  }
                />
              ))}
            </Steps>
          </Card>
        </Col>

        {/* Right Column - History Timeline */}
        <Col xs={24} lg={8}>
          <Card 
            title={<><HistoryOutlined /> Full History</>} 
            bordered={false}
            style={{ borderRadius: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}
            headStyle={{ borderBottom: '1px solid #f0f0f0', padding: '16px 24px' }}
            bodyStyle={{ padding: '24px 24px 8px 24px' }}
          >
            {instance.history && instance.history.length > 0 ? (
              <Timeline>
                {instance.history.map((item, idx) => {
                  const color = item.action === 'approved' ? 'green' 
                            : item.action === 'rejected' ? 'red' 
                            : item.action === 'escalated' ? 'orange' 
                            : 'blue';
                  
                  return (
                    <Timeline.Item key={idx} color={color}>
                      <div style={{ marginBottom: 4 }}>
                        <Text strong style={{ textTransform: 'capitalize' }}>{item.action}</Text>
                        <Text type="secondary"> by </Text>
                        <Text strong>{usersMap[item.user]?.name || "System"}</Text>
                      </div>
                      <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 8 }}>
                        {item.timestamp ? dayjs(item.timestamp).format("DD MMM YYYY, hh:mm A") : "-"}
                      </div>
                      {item.comment && (
                        <div style={{ background: '#f5f5f5', padding: '8px 12px', borderRadius: 6, fontSize: 13, borderLeft: `3px solid ${color}` }}>
                          {item.comment}
                        </div>
                      )}
                    </Timeline.Item>
                  );
                })}
              </Timeline>
            ) : (
              <Empty description="No history available yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </Card>
        </Col>
      </Row>

      {/* Action Modal */}
      <Modal
        open={actionModal.visible}
        title={
          <Space>
            {actionModal.type === "approved" && <CheckCircleOutlined style={{ color: '#52c41a' }} />}
            {actionModal.type === "rejected" && <CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
            {actionModal.type === "escalated" && <RightCircleOutlined style={{ color: '#faad14' }} />}
            <span style={{ textTransform: 'capitalize' }}>{actionModal.type} Request</span>
          </Space>
        }
        onOk={handleActionOk}
        onCancel={handleActionCancel}
        confirmLoading={actionLoading}
        okText={`Confirm ${actionModal.type}`}
        okButtonProps={{
          type: actionModal.type === "approved" ? "primary" : actionModal.type === "rejected" ? "primary" : "default",
          danger: actionModal.type === "rejected",
          disabled: decision.blocked || decision.loading,
          style: { borderRadius: 6 }
        }}
        cancelButtonProps={{ style: { borderRadius: 6 } }}
        width={decision.form ? 960 : undefined}
      >
        <div style={{ padding: '16px 0' }}>
          {decision.error && (
            <Alert type="warning" showIcon message={decision.error} style={{ marginBottom: 12 }} />
          )}
          {decision.form && (
            <>
              <ApproverPaymentSplits
                data={decision.form}
                value={decision.splits}
                onChange={decision.setSplits}
              />
              {decision.errors.length > 0 && (
                <Alert
                  type="error"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message="Finish the payment breakdown before approving"
                  description={
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {decision.errors.map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                  }
                />
              )}
              <Divider style={{ margin: '12px 0' }} />
            </>
          )}
          <Text style={{ display: 'block', marginBottom: 8 }}>
            Please provide a comment for this action {actionModal.type !== 'approved' && <Text type="danger">*</Text>}:
          </Text>
          <Input.TextArea
            rows={4}
            placeholder={actionModal.type === 'approved' ? "Optional remarks..." : "Reason for this action is required..."}
            value={actionComment}
            onChange={e => setActionComment(e.target.value)}
            style={{ borderRadius: 8 }}
          />
        </div>
      </Modal>
    </div>
  );
};

export default ApprovalInstanceView;
